import React, { useEffect, useMemo, useState } from "react";
import { authFetch, isAdmin } from "../../auth";
import { formatDateTimeText, getDefaultDateTimeRange, toUtcDateTimeQueryValue } from "./logHelpers";
import { fetchCustomerOptions, findCustomerName, formatCustomerLabel } from "./logFilterOptions";

const PAGE_SIZE = 20;

export function SystemOperationLogPage() {
  const admin = isAdmin();
  const defaultTimeRange = useMemo(() => getDefaultDateTimeRange(), []);

  const [opType, setOpType] = useState("");
  const [module, setModule] = useState("");
  const [action, setAction] = useState("");
  const [keyword, setKeyword] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [optionError, setOptionError] = useState("");
  const [userId, setUserId] = useState("");
  const [startTime, setStartTime] = useState(defaultTimeRange.startTime);
  const [endTime, setEndTime] = useState(defaultTimeRange.endTime);

  const [page, setPage] = useState(1);
  const [queryVersion, setQueryVersion] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  useEffect(() => {
    if (!admin) return;
    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  useEffect(() => {
    loadLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, queryVersion]);

  async function loadCustomerOptions() {
    try {
      setCustomers(await fetchCustomerOptions());
      setOptionError("");
    } catch (err) {
      setCustomers([]);
      setOptionError(err?.message || "客户列表加载失败");
    }
  }

  async function loadLogs(targetPage) {
    setLoading(true);
    setError("");

    try {
      const qs = new URLSearchParams({
        page: String(targetPage),
        pageSize: String(PAGE_SIZE)
      });

      if (opType.trim()) qs.set("opType", opType.trim());
      if (module.trim()) qs.set("module", module.trim());
      if (action.trim()) qs.set("action", action.trim());
      if (keyword.trim()) qs.set("keyword", keyword.trim());
      if (userId.trim()) qs.set("userId", userId.trim());
      if (admin && customerId.trim()) qs.set("customerId", customerId.trim());

      const startUtc = toUtcDateTimeQueryValue(startTime);
      const endUtc = toUtcDateTimeQueryValue(endTime);
      if (startUtc) qs.set("startTimeUtc", startUtc);
      if (endUtc) qs.set("endTimeUtc", endUtc);

      const res = await authFetch(`/api/system-operation-logs?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "系统操作日志查询失败");
      }

      const data = await res.json();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "系统操作日志查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setExpandedId(null);
    setPage(1);
    setQueryVersion((v) => v + 1);
  }

  function onReset() {
    setOpType("");
    setModule("");
    setAction("");
    setKeyword("");
    setCustomerId("");
    setUserId("");
    const nextTimeRange = getDefaultDateTimeRange();
    setStartTime(nextTimeRange.startTime);
    setEndTime(nextTimeRange.endTime);
    setExpandedId(null);
    setError("");
    setPage(1);
    setQueryVersion((v) => v + 1);
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white">系统操作日志</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-2">
              <label className="form-label">操作类型</label>
              <input className="form-control" value={opType} onChange={(e) => setOpType(e.target.value)} placeholder="如 AUTH_LOGIN_SUCCESS" />
            </div>
            <div className="col-md-2">
              <label className="form-label">模块</label>
              <input className="form-control" value={module} onChange={(e) => setModule(e.target.value)} placeholder="如 auth / permission" />
            </div>
            <div className="col-md-2">
              <label className="form-label">动作</label>
              <input className="form-control" value={action} onChange={(e) => setAction(e.target.value)} placeholder="如 login / update" />
            </div>
            <div className="col-md-2">
              <label className="form-label">操作人ID</label>
              <input className="form-control" value={userId} onChange={(e) => setUserId(e.target.value)} />
            </div>
            {admin && (
              <div className="col-md-2">
                <label className="form-label">客户</label>
                <select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">全部客户</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={String(customer.id)}>
                      {formatCustomerLabel(customer)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-4">
              <label className="form-label">关键字</label>
              <input className="form-control" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="操作类型 / 明细" />
            </div>
            <div className="col-md-3">
              <label className="form-label">开始时间</label>
              <input className="form-control" type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">结束时间</label>
              <input className="form-control" type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询"}
              </button>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" type="button" onClick={onReset} disabled={loading}>
                重置
              </button>
            </div>
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {optionError && <div className="status-banner warn mt-3">{optionError}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>时间</th>
                  <th>操作人</th>
                  <th>模块</th>
                  <th>动作</th>
                  <th>操作类型</th>
                  <th>目标客户</th>
                  <th>目标名称</th>
                  <th>详情</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <div className="status-banner empty m-0">暂无系统操作日志</div>
                    </td>
                  </tr>
                )}
                {items.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td>{formatDateTimeText(row.opTime)}</td>
                      <td>{row.operatorName || "-"}</td>
                      <td>{row.module || "-"}</td>
                      <td>{row.action || "-"}</td>
                      <td>{row.opType || "-"}</td>
                      <td>{findCustomerName(customers, row.targetCustomerId)}</td>
                      <td>{row.targetName || "-"}</td>
                      <td>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          type="button"
                          onClick={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                        >
                          {expandedId === row.id ? "收起" : "展开"}
                        </button>
                      </td>
                    </tr>
                    {expandedId === row.id && (
                      <tr>
                        <td colSpan={8}>
                          <pre className="mb-0" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                            {row.detail || "-"}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              共 {total} 条，当前第 {page}/{pageCount} 页
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page >= pageCount || loading}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
